<template>
    <div>
        <b-list-group class="list-group-accent">
            <b-list-group-item
                class="list-group-item-accent-secondary bg-light text-center font-weight-bold text-muted text-uppercase small"
                >{{ $t('targets.files') }}</b-list-group-item
            >

            <b-list-group-item class="list-group-item-accent-light list-group-item-divider">
                <b-form-group class="overflow-hidden">
                    <b-row>
                        <b-col sm="12" lg="12">
                            <b-form-group :label="$t('form.label.targetFile')" label-class="font-weight-bold">
                                <b-form-file
                                    ref="file-input"
                                    v-model="selectedFile"
                                    :placeholder="$t('form.placeholder.chooseFile')"
                                    accept=".doc, .docx, .xls, .xlsx, .ppt, .pptx, .pdf, .png, .jpg, .ods, .odt, .odp, .gif, .txt, .pst, .ost"
                                    @change="onFileSelected"
                                    :browse-text="$t('form.button.search')"
                                ></b-form-file>
                                <small v-if="fileIsTooLarge" class="text-danger">{{
                                    $t('targets.informations.sizeFileMax')
                                }}</small>
                            </b-form-group>

                            <ButtonLoader
                                class="float-right"
                                type="submit"
                                variant="primary"
                                :disabled="!selectedFile || isLoading || fileIsTooLarge"
                                @click="onUpload"
                                :loader="isLoading"
                                >{{ $t('form.button.save') }}</ButtonLoader
                            >
                        </b-col>
                        <b-col>
                            <b-progress class="m-1" v-if="value > 0" :value="value" :max="max" show-progress animated />
                        </b-col>
                    </b-row>
                </b-form-group>

                <b-table
                    hover
                    striped
                    bordered
                    small
                    :items="files"
                    :busy="isLoading"
                    :show-empty="true"
                    :fields="fields"
                    :empty-text="$t('targets.emptyTable')"
                >
                    <template #thead-top>
                        <tr>
                            <th colspan="3" class="text-center align-middle">
                                {{ $t('targets.files') }}
                            </th>
                        </tr>
                    </template>

                    <template #cell(name)="{ item }">
                        <span>
                            <b-button variant="link" :title="$t('targets.fileDownload')" @click="onDownload(item)">{{
                                item.name
                            }}</b-button>
                        </span>
                    </template>

                    <template #cell(delete)="{ item }">
                        <div class="td-action text-center">
                            <b-link :title="$t('form.button.delete')">
                                <i class="fa fa-trash fa-lg text-danger" @click="fileDelete(item.id)"></i>
                            </b-link>
                        </div>
                    </template>

                    <template #table-busy>
                        <div class="text-center my-2">
                            <b-spinner class="align-middle"></b-spinner>
                        </div>
                    </template>
                </b-table>
            </b-list-group-item>
        </b-list-group>
    </div>
</template>

<script>
import api from '@/services/api'
import Swal from 'sweetalert2'
import notification, { success, danger } from '@/lib/notification'
import i18n from '@/i18n'
import { deleteConfirmationWithLoading } from '@/config/sweet-alert'
import ButtonLoader from '@/views/components/button-loader'

notification.init()

export default {
    name: 'Files',
    components: {
        ButtonLoader,
    },
    data() {
        return {
            fields: [
                {
                    key: 'name',
                    label: i18n.t('form.label.name'),
                },
                {
                    key: 'delete',
                    label: i18n.t('form.button.delete'),
                },
            ],
            isLoading: true,
            selectedFile: null,
            fileIsTooLarge: false,
            value: 0,
            max: 100,
            files: [],
            id: this.$route.params.actionPlanId,
        }
    },
    mounted() {
        this.getFiles().then(() => {
            this.isLoading = false
        })
    },
    methods: {
        getFiles() {
            return api
                .get(`/action-plans/${this.id}/uploads`, {
                    params: {
                        all: 1,
                        ascending: 1,
                        orderBy: 'name',
                    },
                })
                .then((resp) => {
                    this.files = resp.data.data
                })
                .catch((error) => {
                    console.log(error)
                })
        },
        fileDelete(id) {
            Swal({
                ...deleteConfirmationWithLoading,
                preConfirm: () => {
                    return api
                        .delete(`/action-plans/${id}/uploads`)
                        .then(() => {
                            success(
                                i18n.t('notification.deletedSuccessfully', {
                                    item: i18n.t('form.label.file'),
                                })
                            )
                            this.getFiles()
                        })
                        .catch((error) => {
                            danger(error)
                        })
                },
            })
        },
        onFileSelected(event) {
            this.selectedFile = event.target.files[0]
        },
        onUpload() {
            this.isLoading = true
            const formData = new FormData()
            formData.append('file', this.selectedFile, this.selectedFile.file)

            api.post(`/action-plans/${this.id}/uploads`, formData, {
                onUploadProgress: (uploadEvent) => {
                    this.value = Math.round((uploadEvent.loaded / uploadEvent.total) * this.max)
                },
            })
                .then(() => {
                    setTimeout(() => {
                        this.value = 0
                        success(i18n.t('notification.loadedFileWithSuccess'))
                        this.getFiles()
                    }, 2000)
                })
                .catch(() => {
                    setTimeout(() => {
                        this.value = 0
                        danger(i18n.t('targets.informations.errorFileLoading'))
                    }, 2000)
                })
                .finally(() => {
                    this.isLoading = false
                    this.selectedFile = null
                })
        },
        onDownload(item) {
            this.isLoading = true

            api.get(`/action-plans/${item.id}/downloads`, { responseType: 'blob' })
                .then((resp) => {
                    const url = window.URL.createObjectURL(new Blob([resp.data]))
                    let link = document.createElement('a')

                    link.href = url
                    link.setAttribute('download', item.name)
                    document.body.appendChild(link)
                    link.click()

                    setTimeout(() => {
                        success(i18n.t('notification.downloadedFileWithSuccess'))
                    }, 2000)
                })
                .catch(() => {
                    setTimeout(() => {
                        danger(i18n.t('targets.informations.errorFileLoading'))
                    }, 2000)
                })
                .finally(() => (this.isLoading = false))
        },
    },
    watch: {
        selectedFile: function () {
            const MAX_FILE_SIZE_ALLOWED = 20000000
            if (this.selectedFile) this.fileIsTooLarge = this.selectedFile.size > MAX_FILE_SIZE_ALLOWED
        },
    },
}
</script>
